import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { FadeOutAnimation } from '../../animations/fade-out.animation';
import { ArchiveDNSResultsService } from '../../services/archive-dns-results.service';
import { DecisionTransportService } from '../../services/decision-transport.service';
import { InstructionService } from '../../services/instruction.service';
import { LoadConfigService } from '../../services/load-config.service';
import { LoadNeedsphereService } from '../../services/load-needsphere.service';
import { LoadStimuliService } from '../../services/load-stimuli.service';
import { StudyNavigationService } from '../../services/study-navigation.service';
import { UtilsService } from '../../services/utils.service';

@Component({
    selector: 'app-priming-display',
    templateUrl: './priming-display.component.html',
    styleUrls: ['./priming-display.component.scss'],
    animations: FadeOutAnimation,
    standalone: false
})
export class PrimingDisplayComponent implements OnInit, OnDestroy {
  @Input() intermissionButtonLabel: string;
  showIntermission = false;
  intermissionPoints = [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  stimuli: any[];
  keyLabels: string[];
  decisionKeys: string[];
  // timer
  showTimer = false;
  timer: number;
  timerSub: Subscription;

  primingInstruction: string;
  displayText: string;

  // priming
  displayPrimer: string;
  displayValue: string;
  primer: boolean;
  primer_blank: boolean;
  target: boolean;
  target_blank: boolean;
  madeDecision: boolean;

  // initialized with true, so that the "X" is not shown when loading the page
  answerCorrect = true;
  firstAnswerCorrect: boolean = undefined;
  timeUntilFirstReaction: number = undefined;
  category: string[] = ['trust', 'links 2', 'distrust', 'rechts 2'];

  // information about block:
  blockName: string;
  blockNr: number;
  practiceMode = false;
  readingMode = false;
  brandImage = undefined;
  brandName = undefined;

  // sets the type between default, brand and agreement
  type = 'priming';
  mode = 'IAT';

  // timer
  timeoutBetweenStimuli = 1200;
  timeoutPrimerBlank = 500;
  timeoutTarget = 1000;
  timeoutTargetGone = 2000;

  // used to iterate through needs
  id = 0;
  finishedTrial = false;

  currentTime: number;
  runningTimer;

  // flag for automatically proceeding to next item in needs array
  dontProceed = true;
  isVisible = true;

  private studies = this.loadConfigService.getRandomizedStudies();
  private lng = this.instructionService.getLanguageIdentifier();

  constructor(
    private loadConfigService: LoadConfigService,
    private loadNeedsphereService: LoadNeedsphereService,
    private loadStimuli: LoadStimuliService,
    private archiveDNSResultsService: ArchiveDNSResultsService,
    private decisionTransporter: DecisionTransportService,
    private navigationService: StudyNavigationService,
    private instructionService: InstructionService,
    private utilsService: UtilsService,
    @Inject('BASE_URL') private baseUrl: string
  ) {}

  ngOnInit(): void {
    // needed to make decision via mouse work
    this.decisionTransporter.decisionEvent.subscribe((decision: number | string) => {
      this.nextItem(decision);
    });

    this.getIATConfig();

    this.archiveDNSResultsService.initializeArchivedIATResults({
      mode: this.mode,
      IATtype: this.type,
      blockID: this.blockNr
    });

    this.setKeys(this.type);
    this.setStimuli();

    this.nextItem(0);

    this.getIntermissionPoints();

    if (!this.intermissionButtonLabel) {
      this.intermissionButtonLabel = this.instructionService.getTranslationsForTerm('INTERMISSION_CONTINUE_BUTTON');
    }

    this.primingInstruction = this.instructionService.getTranslationsForTerm('PRIMING_INSTRUCTION');
  }

  ngOnDestroy() {
    clearInterval(this.runningTimer);
  }

  getIntermissionPoints() {
    if (['persona', 'brand', 'modular', 'priming'].includes(this.type) && !this.practiceMode && !this.readingMode) {
      const amountOfStimuli = this.stimuli.length;
      const sectionStops = 5;

      let intermissionBreaks = [];

      let i = sectionStops;
      while (i < 10) {
        intermissionBreaks.push(i);

        i += sectionStops;
      }

      // need to check if we went over repetitions (might be due to floating point arithmetic)
      if (intermissionBreaks[intermissionBreaks.length - 1] > 10) {
        intermissionBreaks = intermissionBreaks.slice(0, intermissionBreaks.length - 1);
      }

      this.intermissionPoints = intermissionBreaks.map(point => Math.floor(point * (amountOfStimuli / 10)));
    }
  }

  startIntermission() {
    clearInterval(this.runningTimer);
    this.showIntermission = true;
  }

  stopIntermission() {
    this.showTimer = true;
    this.timer = 3;
    this.timerSub = interval(1000).subscribe(() => {
      this.timer--;
      if (this.timer === 0) {
        this.showTimer = false;
        this.showIntermission = false;

        setTimeout(() => {
          this.primer = false;
          this.primer_blank = true;
        }, this.timeoutPrimerBlank);
        setTimeout(() => {
          this.primer_blank = false;
          this.target = true;
          this.startTimer();
        }, this.timeoutTarget);
        setTimeout(() => {
          if (this.madeDecision == false) {
            this.target = false;
            this.target_blank = true;
          }
        }, this.timeoutTargetGone);

        this.timerSub.unsubscribe();
      }
    });
    // // trial is finished
    // if (this.id === this.stimuli.length) {
    //   this.finishedTrial = true;
    //   clearInterval(this.runningTimer);
    //   this.navigationService.renderNext();
    // }
  }

  private setKeys(test) {
    this.decisionKeys = this.loadConfigService.getDecisionKeysIAT(test);
  }

  private setStimuli() {
    this.stimuli = this.loadStimuli.getStimuli(
      this.type,
      this.readingMode,
      this.practiceMode,
      this.mode,
      // #customSphere Check code and possible adjustments.
      undefined,
      this.instructionService.getLanguageIdentifier()
    );

    this.currentTime = this.startTimer();

    if (!this.dontProceed) {
      this.resetTimer();
    }
  }

  private getIATConfig() {
    const IATConfig = this.navigationService.IATconfig;

    this.navigationService.setCategoriesForFirstIAT(this.utilsService.filterConfigs(this.studies), this.lng);

    this.practiceMode = IATConfig.practiceMode;
    this.readingMode = IATConfig.readingMode;

    this.type = 'priming';
    this.mode = IATConfig.mode;

    // check if needed
    this.blockName = IATConfig.blockName;
    this.blockNr = IATConfig.blockNr;

    this.category = IATConfig.stimuliPositions;

    if (this.mode === 'reading') {
      this.dontProceed = true;
    }

    if ((this.mode === 'IAT' || this.mode === 'practice') && IATConfig['proceedAutomatically'] === false) {
      this.dontProceed = true;
    }
  }

  checkAnswerAndLoadNextItem(decision: number | string, receivedUserInput = true) {
    this.madeDecision == true;
    // check if the answer is correct and update the member variable
    let correctAnswer;
    // check the categories, element 0 and 1 are on the left, 2 and 3 on the right
    // string matching
    if (this.stimuli[this.id].type == this.category[0] || this.stimuli[this.id].type == this.category[1]) {
      correctAnswer = 0;
    } else {
      correctAnswer = 1;
    }

    if (decision == correctAnswer) {
      // saves information whether first answer was correct or not
      if (this.firstAnswerCorrect == undefined) {
        this.firstAnswerCorrect = true;
      }

      this.answerCorrect = true;
      this.saveTime(decision, receivedUserInput);
      this.isVisible = false;

      // Interstimulus Interval (Neely (original paper) 3000ms)
      const timer = setTimeout(() => {
        this.nextItem(decision, receivedUserInput);
        clearTimeout(timer);
      }, this.timeoutBetweenStimuli);
    } else {
      if (this.firstAnswerCorrect == undefined) {
        this.firstAnswerCorrect = false;
      }

      this.answerCorrect = false;
      this.saveTime(decision, receivedUserInput);
      this.isVisible = false;

      // Interstimulus Interval (Neely (original paper) 3000ms)
      const timer = setTimeout(() => {
        this.nextItem(decision, receivedUserInput);
        clearTimeout(timer);
      }, this.timeoutBetweenStimuli);
    }
  }
  // TODO:#code
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  nextItem = (decision: number | string, receivedUserInput = true) => {
    this.isVisible = true;
    this.timeUntilFirstReaction = undefined;
    this.firstAnswerCorrect = undefined;
    this.primer = true;
    this.target = false;
    this.primer_blank = false;
    this.target_blank = false;
    this.madeDecision = false;

    if (this.id < this.stimuli.length) {
      this.id++;

      if (this.intermissionPoints.includes(this.id)) {
        return this.startIntermission();
      }

      if (!this.dontProceed) {
        this.resetTimer();
      }

      // trial is finished
      if (this.id === this.stimuli.length) {
        this.finishedTrial = true;
        clearInterval(this.runningTimer);
        this.navigationService.renderNext();
      }

      if (this.id < this.stimuli.length) {
        this.displayPrimer = this.stimuli[this.id].value.primer;
        this.displayValue = this.stimuli[this.id].value.value;
        // document.getElementById('primingTarget').style.color = '#FF0000';
      }
    } else {
      // trial is finished
      clearInterval(this.runningTimer);
      this.finishedTrial = true;
    }

    setTimeout(() => {
      this.primer = false;
      this.primer_blank = true;
    }, this.timeoutPrimerBlank);
    setTimeout(() => {
      this.primer_blank = false;
      this.target = true;
      this.startTimer();
    }, this.timeoutTarget);
    setTimeout(() => {
      if (this.madeDecision == false) {
        this.target = false;
        this.target_blank = true;
      }
    }, this.timeoutTargetGone);
  };

  private startTimer = () => {
    const time = new Date().getTime();
    this.currentTime = time;
    return time;
  };

  private endTimer = () => {
    return new Date().getTime() - this.currentTime;
  };

  private saveTime = (decision: number | string, receivedUserInput: boolean) => {
    const reactionTime = this.endTimer();

    if (this.timeUntilFirstReaction == undefined) {
      this.timeUntilFirstReaction = this.endTimer();
    }

    if (this.answerCorrect == true) {
      this.archiveDNSResultsService.savePrimingDecision({
        IATtype: this.type,
        mode: this.mode,
        stimulus: this.stimuli[this.id],
        reactionTimeUntilFirstDecision: this.timeUntilFirstReaction,
        reactionTimeUntilCorrectDecision: reactionTime,
        madeCorrectDecision: this.firstAnswerCorrect,
        receivedUserInput
      });
    } else {
      this.archiveDNSResultsService.savePrimingDecision({
        IATtype: this.type,
        mode: this.mode,
        stimulus: this.stimuli[this.id],
        reactionTimeUntilFirstDecision: this.timeUntilFirstReaction,
        reactionTimeUntilCorrectDecision: -1,
        madeCorrectDecision: this.firstAnswerCorrect,
        receivedUserInput
      });
    }
  };

  private setTimer(time: number, decision: number | string, receivedUserInput: boolean): void {
    this.runningTimer = setInterval(() => {
      this.nextItem(decision, receivedUserInput);
    }, time);
  }

  private resetTimer = () => {
    if (this.runningTimer) {
      clearInterval(this.runningTimer);
    }
    this.setTimer(this.loadConfigService.getProceedAfter(), 0, false);
  };
}
